if (!window.Eurus.loadedScript.has('featured-collection.js')) {
  window.Eurus.loadedScript.add('featured-collection.js');

  requestAnimationFrame(() => {
    document.addEventListener('alpine:init', () => {
      Alpine.data('xFeaturedCollection', (sectionId, pageParam, container) => ({
        sectionId: sectionId,
        pageParam: pageParam,
        currentTab: 1,
        loading: true,
        loaded: [],
        init() {
          this.syncActiveHeaderArrows();

          document.addEventListener(`eurus:${this.sectionId}:splide-ready`, () => {
            this.syncActiveHeaderArrows();
          });
        },
        select(index) {
          this.currentTab = index;
          this.unlockHeaderArrows();
          this.loadData(index);
          this.resetActiveManualSlider();
          this.recoverActiveSlider();
        },
        loadData(index) {
          const selectedPage = index - 1;
          if (!this.loaded.includes(selectedPage)) {
            this.loading = true;
            
            let url = `${window.location.pathname}?section_id=${this.sectionId}&${this.pageParam}=${index}`;
            fetch(url, {
              method: 'GET'
            }).then(
              response => response.text()
            ).then(responseText => {
              const html = (new DOMParser()).parseFromString(responseText, 'text/html');
              const contentId = `x-fc-${this.sectionId}-${index}`;
              const newContent = html.getElementById(contentId);
              if (newContent && !document.getElementById(contentId)) {
                container.appendChild(newContent);
                if (window.Alpine?.initTree) {
                  window.Alpine.initTree(newContent);
                }
                this.loaded.push(selectedPage);
                this.recoverActiveSlider();
              }
              this.loading = false;
            })
          }
        },
        getTabGroupSelector(index = this.currentTab) {
          const escapedSectionId = window.CSS?.escape ? CSS.escape(this.sectionId) : this.sectionId;
          const escapedIndex = window.CSS?.escape ? CSS.escape(String(index)) : String(index);

          return `[data-featured-collection-group="${escapedSectionId}"][data-featured-collection-tab="${escapedIndex}"]`;
        },
        getActiveCollectionGroup() {
          const currentGroup = container.querySelector(this.getTabGroupSelector());
          if (currentGroup) return currentGroup;

          return container.querySelector(`#x-fc-${this.sectionId}-${this.currentTab}`);
        },
        getActiveSplideRoot() {
          const group = this.getActiveCollectionGroup();
          if (!group) return null;

          return group.querySelector(`[data-featured-collection-slider="${this.sectionId}"][data-featured-collection-slider-tab="${this.currentTab}"]`) || group.querySelector('.collection.splide') || null;
        },
        unlockHeaderArrows() {
          const headerPrev = container.querySelector('[data-featured-collection-header-prev]');
          const headerNext = container.querySelector('[data-featured-collection-header-next]');
          const headerArrows = headerPrev?.closest('.featured-collection__header-arrows');

          [headerPrev, headerNext].forEach(button => {
            if (!button) return;
            button.disabled = false;
            button.setAttribute('aria-disabled', 'false');
          });

          if (headerArrows) {
            headerArrows.hidden = false;
            headerArrows.classList.remove('hidden');
          }
        },
        syncHeaderArrows() {
          const headerPrev = container.querySelector('[data-featured-collection-header-prev]');
          const headerNext = container.querySelector('[data-featured-collection-header-next]');
          if (!headerPrev || !headerNext) return;

          const headerArrows = headerPrev.closest('.featured-collection__header-arrows');
          const canNavigate = this.canManualSliderMove();
          const parts = this.getManualSliderParts();
          const currentIndex = parts ? Number(parts.root.dataset.featuredCollectionManualIndex || 0) : 0;
          const endIndex = parts ? this.getManualSliderEndIndex(parts) : 0;
          const prevDisabled = !canNavigate || currentIndex <= 0;
          const nextDisabled = !canNavigate || currentIndex >= endIndex;

          if (headerArrows) {
            headerArrows.hidden = !canNavigate;
            headerArrows.classList.toggle('hidden', !canNavigate);
          }

          headerPrev.disabled = prevDisabled;
          headerNext.disabled = nextDisabled;
          headerPrev.setAttribute('aria-disabled', prevDisabled ? 'true' : 'false');
          headerNext.setAttribute('aria-disabled', nextDisabled ? 'true' : 'false');
        },
        resetActiveManualSlider() {
          this.$nextTick(() => {
            requestAnimationFrame(() => {
              const parts = this.getManualSliderParts();
              if (!parts) return;

              parts.root.dataset.featuredCollectionManualIndex = '0';
              parts.list.style.transition = '';
              parts.list.style.transform = `translateX(-${this.getManualSliderOffset(parts, 0)}px)`;
            });
          });
        },
        recoverActiveSlider() {
          [0, 100, 250, 500, 900, 1400].forEach(delay => {
            setTimeout(() => this.syncActiveHeaderArrows(), delay);
          });
        },
        syncActiveHeaderArrows() {
          this.$nextTick(() => {
            requestAnimationFrame(() => {
              this.bindManualDragEvents(this.getActiveSplideRoot());
              this.syncHeaderArrows();
            });
          });
        },
        goActiveSlide(direction) {
          this.moveManualSlider(direction);
          this.syncActiveHeaderArrows();
        },
        getManualSliderParts() {
          const root = this.getActiveSplideRoot();
          const track = root?.querySelector('.splide__track');
          const list = root?.querySelector('.splide__list');
          const slides = list ? Array.from(list.children).filter(slide => slide.classList.contains('splide__slide') && !slide.classList.contains('splide__slide--clone')) : [];

          if (!root || !track || !list || slides.length <= 1) return null;

          return { root, track, list, slides };
        },
        canManualSliderMove() {
          const parts = this.getManualSliderParts();
          if (!parts) return false;

          return this.getManualSliderMaxOffset(parts) > 2;
        },
        getTrackPadding(parts) {
          const style = window.getComputedStyle(parts.track);

          return {
            left: parseFloat(style.paddingLeft) || 0,
            right: parseFloat(style.paddingRight) || 0
          };
        },
        getManualSliderMaxOffset(parts) {
          const { track, slides } = parts;
          const lastSlide = slides[slides.length - 1];
          if (!lastSlide) return 0;

          const padding = this.getTrackPadding(parts);
          const visibleWidth = track.clientWidth - padding.left - padding.right;

          return Math.max((lastSlide.offsetLeft + lastSlide.offsetWidth) - visibleWidth, 0);
        },
        getManualSliderEndIndex(parts) {
          const maxOffset = this.getManualSliderMaxOffset(parts);
          const lastIndex = parts.slides.findIndex(slide => slide.offsetLeft >= maxOffset);

          return lastIndex > -1 ? lastIndex : parts.slides.length - 1;
        },
        moveManualSlider(direction) {
          const parts = this.getManualSliderParts();
          if (!parts || !this.canManualSliderMove()) return false;

          const { root, list, slides } = parts;
          const currentIndex = Number(root.dataset.featuredCollectionManualIndex || 0);
          const maxOffset = this.getManualSliderMaxOffset(parts);
          const endIndex = this.getManualSliderEndIndex(parts);
          let nextIndex;

          if (direction === 'prev') {
            if (currentIndex <= 0) return false;
            nextIndex = currentIndex - 1;
          } else {
            if (currentIndex >= endIndex) return false;
            nextIndex = currentIndex + 1;
          }

          const nextOffset = Math.min(slides[nextIndex]?.offsetLeft || 0, maxOffset);
          root.dataset.featuredCollectionManualIndex = String(nextIndex);
          list.style.transition = 'transform 650ms cubic-bezier(0.22, 0.61, 0.36, 1)';
          list.style.transform = `translateX(-${nextOffset}px)`;

          return true;
        },
        getManualSliderOffset(parts, index) {
          const { slides } = parts;
          const maxOffset = this.getManualSliderMaxOffset(parts);
          const padding = this.getTrackPadding(parts);

          return Math.min(Math.max((slides[index]?.offsetLeft || 0) - padding.left, 0), maxOffset);
        },
        bindManualDragEvents(root) {
          if (!root) return;
          if (root.dataset.featuredCollectionManualDragBound === 'true') return;

          const track = root.querySelector('.splide__track');
          const list = root.querySelector('.splide__list');
          if (!track || !list) return;

          let startX = 0;
          let currentX = 0;
          let startOffset = 0;
          let dragging = false;
          let moved = false;
          let pointerId = null;
          let pendingOffset = 0;
          let dragFrame = null;

          const getPointX = event => event.touches?.[0]?.clientX ?? event.clientX;
          const setDragOffset = offset => {
            pendingOffset = offset;
            if (dragFrame) return;

            dragFrame = requestAnimationFrame(() => {
              list.style.transform = `translateX(-${pendingOffset}px)`;
              dragFrame = null;
            });
          };
          const endDrag = event => {
            if (!dragging) return;

            if (pointerId !== null && event.pointerId !== undefined && event.pointerId !== pointerId) return;

            const delta = currentX - startX;
            dragging = false;
            pointerId = null;
            if (dragFrame) {
              cancelAnimationFrame(dragFrame);
              dragFrame = null;
            }
            track.classList.remove('featured-collection-manual-dragging');

            if (Math.abs(delta) > 90) {
              moved = true;
              list.style.transition = '';
              this.moveManualSlider(delta < 0 ? 'next' : 'prev');
            } else {
              moved = false;
              list.style.transition = 'transform 420ms cubic-bezier(0.22, 0.61, 0.36, 1)';
              list.style.transform = `translateX(-${startOffset}px)`;
            }
          };

          root.dataset.featuredCollectionManualDragBound = 'true';

          track.addEventListener('pointerdown', event => {
            if (event.button !== undefined && event.button !== 0) return;
            const parts = this.getManualSliderParts();
            if (!parts || !this.canManualSliderMove()) return;

            dragging = true;
            moved = false;
            pointerId = event.pointerId;
            startX = getPointX(event);
            currentX = startX;
            startOffset = this.getManualSliderOffset(parts, Number(root.dataset.featuredCollectionManualIndex || 0));
            list.style.transition = 'none';
          });

          track.addEventListener('pointermove', event => {
            if (!dragging) return;
            if (pointerId !== null && event.pointerId !== undefined && event.pointerId !== pointerId) return;

            const parts = this.getManualSliderParts();
            if (!parts) return;

            currentX = getPointX(event);
            const delta = currentX - startX;
            const maxOffset = this.getManualSliderMaxOffset(parts);
            const dragOffset = Math.min(Math.max(startOffset - (delta * 0.45), 0), maxOffset);

            if (Math.abs(delta) > 14) {
              moved = true;
              track.classList.add('featured-collection-manual-dragging');
              event.preventDefault();
            }

            setDragOffset(dragOffset);
          });

          track.addEventListener('pointerup', endDrag);
          track.addEventListener('pointercancel', endDrag);
          window.addEventListener('pointerup', endDrag);
          window.addEventListener('pointercancel', endDrag);
          track.addEventListener('dragstart', event => {
            if (dragging) {
              event.preventDefault();
            }
          });
          track.addEventListener('click', event => {
            if (!moved) return;

            event.preventDefault();
            event.stopPropagation();
            moved = false;
          }, true);
        },
        scrollIntoView(element) {
          const scrollableContainer = element.closest('.overflow-x-auto'); 
          const elementPosition = element.offsetLeft;

          scrollableContainer.scroll({
            left: elementPosition,
            behavior: 'smooth'
          });
        }
      }));
    });
  });
}
